import styled from "styled-components"

import {
  usePostTaskAssigned,
  usePostTaskReassigned,
  usePostTaskUnassigned,
} from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { isUnassignedOptionId } from "src/data/taskManagement/taskManagementFilters"
import { usePatchTask } from "src/data/taskManagement/taskManagementQueries"
import { TTask } from "src/data/taskManagement/taskManagementTypes"

import { AssigneeDropdownSelect } from "./AssigneeDropdownSelect"

export function AssigneeColumn({
  assignee,
  taskId,
  orgId,
  homeId,
}: {
  assignee: TTask["assigned_to"]
  taskId: string
  orgId: string
  homeId?: string
}) {
  const patchTask = usePatchTask()
  const { org } = useOrganization()

  const postTaskAssigned = usePostTaskAssigned()
  const postTaskUnassigned = usePostTaskUnassigned()
  const postTaskReassigned = usePostTaskReassigned()

  function handleAssigneeChange(value: string) {
    const postBodyAssignedTo: TTask["assigned_to"] = isUnassignedOptionId(value)
      ? { type: "unassigned" }
      : {
          type: "member",
          assignee_id: value,
        }

    patchTask.mutate(
      {
        path: {
          organization_id: orgId,
          task_id: taskId,
        },
        body: { assigned_to: postBodyAssignedTo },
      },
      {
        onSuccess: (task) => trackTaskAssignment(task),
      }
    )
  }

  function trackTaskAssignment(task: TTask) {
    if (
      assignee.type === "unassigned" &&
      task.assigned_to.type === "member" &&
      org.user_role
    ) {
      return postTaskAssigned.mutate({
        task_type: task.category,
        user_role_assignee: org.user_role,
      })
    }

    if (assignee.type === "member" && task.assigned_to.type === "unassigned") {
      return postTaskUnassigned.mutate({
        task_type: task.category,
      })
    }

    if (
      assignee.type === "member" &&
      task.assigned_to.type === "member" &&
      org.user_role
    ) {
      return postTaskReassigned.mutate({
        task_type: task.category,
        user_role_assignee: org.user_role,
      })
    }
  }

  return (
    <Column>
      <AssigneeDropdownSelect
        loading={patchTask.isLoading}
        initialValue={assignee?.assignee_id ?? ""}
        onChange={handleAssigneeChange}
        homeId={homeId}
      />
    </Column>
  )
}

const Column = styled.div`
  display: flex;
  align-items: start;
`
